// components/molecules/DropdownButton/DropdownButton.tsx
import * as React from 'react';
import { Menu, PaletteColor, Button, Typography, useTheme, Box } from '@mui/material';
import { FaOptionIcon, chevronDownIcon } from '@europrocurement/l2d-icons';

export type DropdownButtonProps = {
    label: string;
    children: React.ReactNode;
    color?: PaletteColor | string;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
    variant?: 'contained' | 'outlined' | 'text';
    style?: React.CSSProperties;
    onOpen?: () => void;
    onClose?: () => void;
    // From parent
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
};

const isPaletteColor = (color: PaletteColor | string): color is PaletteColor =>
    typeof color === 'object' && color !== null && 'main' in color;

export const DropdownButton: React.FunctionComponent<DropdownButtonProps> = function ({
    label,
    children,
    color,
    disabled = false,
    size = 'small',
    variant = 'outlined',
    style = undefined,
    onOpen,
    onClose,
    open,
    onOpenChange,
}) {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const isControlled = open !== undefined;
    const isOpen = isControlled ? open : Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        onOpen?.();
        onOpenChange?.(true);
    };

    const handleClose = () => {
        setAnchorEl(null);
        onOpenChange?.(false);
        onClose?.();
    };

    const buttonColor = React.useMemo(() => {
        if (!color) {
            return theme.palette.primary.main;
        }

        if (isPaletteColor(color)) {
            return color.main;
        }

        return color;
    }, [color, theme.palette.primary.main]);

    const iconSizes = {
        small: '10px',
        medium: '16px',
        large: '24px',
    };

    const iconSize = iconSizes[size];

    const getButtonStyles = React.useMemo((): React.CSSProperties => {
        const baseStyles: React.CSSProperties = {
            ...style,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            padding: '8px 12px',
            margin: 0,
            textTransform: 'none',
            minWidth: 0,
            overflow: 'hidden',
        };

        if (variant === 'outlined') {
            return {
                ...baseStyles,
                backgroundColor: 'transparent',
                color: buttonColor,
                border: `1px solid ${buttonColor}`,
            };
        }

        if (variant === 'text') {
            return {
                ...baseStyles,
                backgroundColor: 'transparent',
                color: buttonColor,
                border: 'none',
            };
        }

        return {
            ...baseStyles,
            backgroundColor: buttonColor,
            color: theme.palette.background.paper,
            border: 'none',
        };
    }, [buttonColor, style, theme.palette.background.paper, variant]);

    return (
        <>
            <Button
                onClick={handleClick}
                disabled={disabled}
                size={size}
                variant={variant}
                style={getButtonStyles}
                sx={{
                    '& .MuiButton-startIcon': { margin: 0 },
                    '& .MuiButton-endIcon': { margin: 0 },
                    minWidth: 0,
                    overflow: 'hidden',
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                        lineHeight: 'inherit',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        minWidth: 0,
                        textAlign: 'left',
                    }}
                >
                    {label}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        marginLeft: '8px',
                    }}
                >
                    <FaOptionIcon
                        {...chevronDownIcon.props}
                        size={undefined}
                        color={buttonColor}
                        optionColor={buttonColor}
                        iconProps={{
                            ...chevronDownIcon.props.iconProps,
                            width: iconSize,
                            height: iconSize,
                            style: {
                                ...chevronDownIcon.props.iconProps?.style,
                                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                            },
                        }}
                        sx={{
                            width: `${iconSize}px`,
                            height: `${iconSize}px`,
                            padding: 0,
                            margin: 0,
                        }}
                    />
                </Box>
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: anchorEl?.offsetWidth || 'auto',
                    },
                }}
            >
                {children}
            </Menu>
        </>
    );
};
